/* eslint-disable no-mixed-operators */
define([
    'lodash',
    'react',
    'componentsCore',
    'coreUtils',
    'santa-core-utils',
    'components',
    'santa-components',
    'skins',
    'mediaPlayer/skins/skins.json',
    // Load Data requirements checkers
    'mediaPlayer/mediaPlayerMaskDataRequirementsChecker'
], function (
    _,
    React,
    componentsCore,
    coreUtils,
    coreUtilsLib,
    components,
    santaComponents,
    skinsPackage,
    skinsJson
) {
    'use strict';

    const consts = coreUtils.mediaConsts;
    const mediaPlayerMixin = components.mediaCommon.mediaLogicMixins.mediaPlayer;
    const timeoutsMixin = coreUtilsLib.timersMixins.timeoutsMixin;

    const MOBILE_INTERACTION_DURATION = 4500;
    const {inlineContentMixin} = santaComponents.mixins;

    const getContainerStyle = (containerStyle, dropShadow, flipStyle) => {
        // In Chrome 79 and Safari 13 flip + rotate on media layer causes the container to stack in-front of inline content components
        // Applying a transform, even one that does nothing fixes it.
        // BUT - if we keep 'clip()' on the bg layer we see a white 'nima'
        const transformBugWorkaround = _.isEmpty(flipStyle) ? {} : {transform: 'rotate(0)'};
        containerStyle = _.assign({}, containerStyle, transformBugWorkaround);
        if (dropShadow.css || dropShadow.svg) {
            containerStyle = _.assign(_.omit(containerStyle, 'boxShadow'), {overflow: 'visible'}, dropShadow.css || {});
        }
        if (_.isEmpty(containerStyle.borderWidth)) {
            return containerStyle;
        }

        const borderWidth = parseInt(containerStyle.borderWidth, 10);
        return _.assign({}, containerStyle, {'margin': `-${borderWidth}px`});
    };

    const getDropShadowStyle = props => {
        const flip = props.compProp.flip;
        const {offsetX, offsetY} = props.compDesign.cssStyle.cssBoxShadow[0];
        const transform = props.flipTransformStyle.transform;
        const result = {height: '100%'};
        const x = -offsetX.value * 2;
        const y = -offsetY.value * 2;

        switch (flip) {
            case 'vertical':
                result.transform = `${transform} translateY(${y}px)`;
                break;
            case 'horizontal':
                result.transform = `${transform} translateX(${x}px)`;
                break;
            case 'both':
                result.transform = `${transform} translate(${x}px, ${y}px)`;
                break;
        }

        return result;
    };

    const getCustomCompCss = (getSkin, props, styleId, baseSkinCss, styleClass) => {
        const result = {};

        if (baseSkinCss && baseSkinCss[styleClass]) {
            result[styleClass] = `${baseSkinCss[styleClass]}
@supports (mask-repeat: no-repeat) or (-webkit-mask-repeat: no-repeat) {
  .${styleClass}_masked {
    visibility: visible;
  }
}`;
        }

        return result;
    };

    getCustomCompCss.cssTypes = {
        getStyleData: santaComponents.santaTypesDefinitions.Component.getStyleData,
        themeData: santaComponents.santaTypesDefinitions.Theme.THEME_DATA,
        siteZoomRatio: santaComponents.santaTypesDefinitions.Mobile.siteZoomRatio,
        invertedZoomRatio: santaComponents.santaTypesDefinitions.Mobile.invertedZoomRatio,
        orientationZoomFix: santaComponents.santaTypesDefinitions.Mobile.orientationZoomFix,
        mobileZoom: santaComponents.santaTypesDefinitions.Mobile.mobileZoom,
        scriptsLocationMap: santaComponents.santaTypesDefinitions.ServiceTopology.scriptsLocationMap
    };

    /**
     * @class components.MediaPlayer
     * @extends {core.skinBasedComp}
     */
    const mediaPlayer = {
        displayName: 'MediaPlayer',
        mixins: [componentsCore.mixins.customSkinBasedComp({customCssFunc: getCustomCompCss}), mediaPlayerMixin, timeoutsMixin, componentsCore.mixins.createChildComponentMixin, inlineContentMixin],

        propTypes: {
            style: santaComponents.santaTypesDefinitions.Component.style.isRequired,
            compDesign: santaComponents.santaTypesDefinitions.Component.compDesign,
            compProp: santaComponents.santaTypesDefinitions.Component.compProp,
            rotationInDegrees: santaComponents.santaTypesDefinitions.Component.rotationInDegrees,
            isMobileDevice: santaComponents.santaTypesDefinitions.Device.isMobileDevice,
            renderParts: santaComponents.santaTypesDefinitions.Media.renderParts,
            layoutContainerClassName: santaComponents.santaTypesDefinitions.Layout.layoutContainerClassName,
            isResponsive: santaComponents.santaTypesDefinitions.RendererModel.isResponsive,
            containerStyle: santaComponents.santaTypesDefinitions.MediaPlayerDesign.containerStyle,
            flipTransformStyle: santaComponents.santaTypesDefinitions.Media.flipTransformStyle,
            mask: santaComponents.santaTypesDefinitions.Media.mask,
            dropShadow: santaComponents.santaTypesDefinitions.Media.dropShadow,
            cssFeatureQueriesSupported: santaComponents.santaTypesDefinitions.BrowserFlags.cssFeatureQueriesSupported,
            //renderFlags
            isMobileView: santaComponents.santaTypesDefinitions.isMobileView
        },
        statics: {
            behaviors: components.mediaCommon.mediaBehaviors.mediaPlayer
        },

        getInitialState() {
            this.inMobileInteractionState = false;
            this.interactionsNotificationList = [];
            this.compDesignOverride = undefined;
        },

        /**
         * Add playerId to all the child components of the player
         * @returns {Object}
         */
        addPropsToChildren(children) {
            const props = {
                interactionsNotificationRegistrar: this.interactionsNotificationRegistrar
            };
            return React.Children.map(children, child => React.cloneElement(child, props));
        },

        interactionsNotificationRegistrar(callback) {
            this.interactionsNotificationList.push(callback);
        },

        /**
         * Toggle hover state to pass to children
         * @param event
         */
        toggleHover(event) {
            //call for a mixin function
            const currentState = this.getMediaState();
            const isPlayEnded = currentState.playbackState === consts.playbackTypes.PLAY_ENDED;
            const isReplayDisabled = this.props.compProp.playerInteraction.allowReplay === false;
            const isInteractionDisabled = isReplayDisabled && isPlayEnded;
            const isHoverEvent = event.type !== 'mouseleave';
            const isHover = isHoverEvent && !isInteractionDisabled;

            this.interactionsNotificationList.forEach(item => item({isHover}));
        },

        notifyMouseMove() {
            this.interactionsNotificationList.forEach(item => item({mouseMove: true}));
            this.inMobileInteractionState = true;
            this.clearTimeout();
            this.setTimeout(() => {this.inMobileInteractionState = false;}, MOBILE_INTERACTION_DURATION);
        },

        /**
         * Handle mouseEnter / mouseLeave actions on player
         * @param {[{action: function, params: array}]?} actions
         * @returns {function(event: MouseEvent)}
         */
        handleHoverAction(actions) {
            return event => {
                this.toggleHover(event);
                actions.forEach(command => {
                    if (command.action) {
                        const params = command.params || [];
                        command.action(...params);
                    }
                });
            };
        },

        getAudioHoverInteraction(key) {
            const action = _.get(this.props.compProp, ['playerAudioInteraction', key]);
            const audioActions = {
                mute: {action: this.mediaVolumeFadeOut, params: [0.5]},
                unmute: {action: this.mediaVolumeFadeIn, params: [0.5]}
            };
            return audioActions[action];
        },

        getVideoHoverInteraction(key) {
            const action = this.props.compProp.playerInteraction[key];
            const videoActions = {
                play: {action: this._playMedia},
                pause: {action: this.pauseMedia}, // always persistent when pausing on hover
                none: null
            };
            return videoActions[action];
        },

        getClickInteraction() {
            const {click} = this.props.compProp.playerInteraction;
            const clickActions = {
                toggle: () => this.togglePlayMedia(),
                play: () => this._playMedia({persist: true})
            };
            return clickActions[click];
        },

        /**
         * rollin/rollout interaction are converted to command actions
         * @returns {Function}
         */
        convertHoverToCommandActions() {
            if (this.props.compProp.playerInteraction.rollOut === 'pause') {
                return () => this.togglePlayMedia();
            }
            if (this.props.compProp.playerInteraction.rollIn === 'play') {
                return () => this._playMedia();
            }
            return _.noop;
        },

        /**
         * checks if to trigger actions - for mobile use
         * initial play || no audio || within hover duration
         * @returns {boolean}}
         */
        shouldTriggerAction() {
            const currentState = this.getMediaState();
            const isFirstPlay = currentState.playbackState === consts.playbackTypes.READY;
            const noAudio = this.props.compDesign.background.mediaRef.hasAudio === false || this.props.compProp.disableAudio;
            return this.inMobileInteractionState || isFirstPlay || noAudio;
        },

        /**
         * handle the touch event and prevent the click from firing
         * @param commandActions
         * @returns {Function}
         */
        getTouchInteraction(commandActions) {
            return event => {
                event.preventDefault();
                this.invokeMobileInteraction(event, commandActions);
            };
        },

        /**
         * handle the click event for mobile
         * @param commandActions
         * @returns {Function}
         */
        getMobileClickInteraction(commandActions) {
            return event => {
                //event.preventDefault();
                this.invokeMobileInteraction(event, commandActions);
            };
        },

        /**
         * invocation simulates hover behavior
         * invocation will trigger the action if initial play, no audio and within hover duration
         * @param event
         * @param commandActions
         */
        invokeMobileInteraction(event, commandActions) {
            if (this.shouldTriggerAction()) {
                commandActions();
            }
            this.notifyMouseMove();
            this.toggleHover(event);
        },

        isMobile() {
            return this.props.isMobileView || this.props.isMobileDevice;
        },

        getControls() {
            const shouldRenderChildren = !!this.props.renderParts.media.video.length;
            return shouldRenderChildren ? this.getChildrenRenderer({children: this.addPropsToChildren(this.props.children)}) : null;
        },

        /**
         * @returns {*} event handlers by platform
         */
        getInteractionEvents() {
            if (this.isMobile()) {
                const clickActions = this.getClickInteraction();
                const commandActions = clickActions || this.convertHoverToCommandActions();
                const eventName = this.props.isMobileDevice ? 'onTap' : 'onClick'; //For mobile preview in desktop browser
                return {
                    [eventName]: this.getMobileClickInteraction(commandActions)
                };
            }

            const rollInActions = [
                this.getVideoHoverInteraction('rollIn'),
                this.getAudioHoverInteraction('rollIn')
            ];

            const rollOutActions = [
                this.getVideoHoverInteraction('rollOut'),
                this.getAudioHoverInteraction('rollOut')
            ];

            return {
                onMouseMove: this.notifyMouseMove,
                onClick: this.getClickInteraction(),
                onMouseEnter: this.handleHoverAction(_.compact(rollInActions)),
                onMouseLeave: this.handleHoverAction(_.compact(rollOutActions))
            };
        },

        getSkinProperties() {
            const children = this.getControls();
            const dropShadow = this.props.dropShadow;
            const container = {
                style: getContainerStyle(this.props.containerStyle, dropShadow, this.props.flipTransformStyle)
            };

            if (dropShadow.svg) {
                container.addChildrenBefore = [
                    santaComponents.utils.createReactElement(
                        'div',
                        {
                            style: getDropShadowStyle(this.props),
                            className: this.classSet({'drop-shadow': true}),
                            dangerouslySetInnerHTML: {__html: dropShadow.svg}
                        }
                    )
                ];
            }

            const refData = {
                '': _.assign({
                    style: this.props.style,
                    className: this.classSet({masked: !!this.props.mask && this.props.cssFeatureQueriesSupported})
                }, this.getInteractionEvents()),

                background: this.createFillLayers({
                    compDesign: this.compDesignOverride || this.props.compDesign,
                    enableVideo: !!children,
                    styleOverrides: this.props.flipTransformStyle
                }),

                container,

                inlineContent: _.assign(
                    {children},
                    this.props.isResponsive && {
                        className: this.props.layoutContainerClassName,
                        style: {
                            height: '100%' // otherwise it will tightly wrap inlineContent's children. We want it to be stretched do dimensions of the root
                        }
                    }
                )
            };

            return refData;
        }
    };

    componentsCore.compRegistrar.register('wysiwyg.viewer.components.MediaPlayer', mediaPlayer);
    skinsPackage.skinsMap.addBatch(skinsJson);

    return mediaPlayer;
});
